<script setup lang="ts">
/**
 * Add or edit a source from the admin UI (ADR 0057 Phase A).
 *
 * The bar is the Knowledge Base flow: pick what it is, say where it is, done.
 * So one screen, not a wizard — tiles, then the one field that matters, then
 * a button. Everything a normal person never changes is behind "More
 * options" with defaults that are right.
 *
 * The form is drawn from the backend's catalogue rather than written here.
 * Which knobs are safe to offer is a server-side decision, and the server
 * ignores any field its catalogue does not declare — a second list in this
 * file would only be able to drift from it.
 *
 * Kinds that need a connection are shown greyed with the reason. A missing
 * tile is a mystery; a greyed one says what the platform can do and why not
 * yet from here.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref, watch } from "vue";
import {
  AdminError,
  type AssistantRow,
  type GrantedResource,
  type IngestRunner,
  type ManagedSource,
  type SourceFormField,
  type SourceKind,
  addSourceFromForm,
  triggerIngest,
  updateSource,
  uploadFile,
} from "../api/admin";
import { useToast } from "../composables/useToast";

const props = defineProps<{
  devUser: string;
  kinds: SourceKind[];
  assistants: AssistantRow[];
  runnerAvailable: boolean;
  runner: IngestRunner;
  /** Sites a probe has confirmed this platform can read. */
  resources: GrantedResource[];
  /** Set to edit; absent to add. */
  editing?: ManagedSource | null;
}>();
const emit = defineEmits<{ (e: "done"): void }>();

const toast = useToast();

const kindKey = ref("");
const slug = ref("");
const values = ref<Record<string, unknown>>({});
const files = ref<File[]>([]);
const moreOpen = ref(false);
const busy = ref(false);
const error = ref("");

const kind = computed(
  () => props.kinds.find((k) => k.key === kindKey.value) ?? null,
);
const mainFields = computed(
  () => kind.value?.fields.filter((f) => !f.advanced) ?? [],
);
const advancedFields = computed(
  () => kind.value?.fields.filter((f) => f.advanced) ?? [],
);

/** The site picked in a "resource" field — a "library" field lists ITS libraries. */
const pickedResource = computed(() => {
  const f = kind.value?.fields.find((x) => x.kind === "resource");
  if (!f) return null;
  const id = Number(values.value[f.key]);
  return props.resources.find((r) => r.id === id) ?? null;
});

const grantedResources = computed(() =>
  props.resources.filter((r) => r.status === "granted"),
);

/** Textareas edit text; the API takes lists. Keep "lines" fields as text here. */
function toInput(f: SourceFormField, v: unknown): unknown {
  if (f.kind === "lines") return Array.isArray(v) ? v.join("\n") : (v ?? "");
  // Selects: "" is the placeholder option; null would select nothing at all.
  if (f.kind === "resource" || f.kind === "library") return v ?? "";
  return v ?? f.default;
}

function resetFor(k: SourceKind | null): void {
  const next: Record<string, unknown> = {};
  const from = props.editing?.form?.values ?? {};
  for (const f of k?.fields ?? []) next[f.key] = toInput(f, from[f.key]);
  values.value = next;
  files.value = [];
  error.value = "";
  moreOpen.value = false;
}

watch(
  () => props.editing,
  (row) => {
    kindKey.value = row?.form?.kind ?? "";
    slug.value = row?.assistant_slug ?? "";
    resetFor(kind.value);
  },
  { immediate: true },
);

function pick(k: SourceKind): void {
  if (!k.available) return;
  kindKey.value = k.key;
  resetFor(k);
}

function onFiles(e: Event): void {
  files.value = Array.from((e.target as HTMLInputElement).files ?? []);
}

const canSubmit = computed(() => {
  if (!kind.value || !slug.value || busy.value) return false;
  if (kind.value.mode === "upload") return files.value.length > 0;
  return mainFields.value
    .filter((f) => f.required)
    .every((f) => String(values.value[f.key] ?? "").trim() !== "");
});

const submitLabel = computed(() => {
  if (props.editing) return "Save";
  if (kind.value?.mode === "upload")
    return files.value.length > 1
      ? `Upload ${files.value.length} files`
      : "Upload";
  return props.runnerAvailable ? "Add and sync" : "Add";
});

async function submit(): Promise<void> {
  if (!kind.value || !canSubmit.value) return;
  busy.value = true;
  error.value = "";
  try {
    if (props.editing) {
      await updateSource(
        props.editing.assistant_slug,
        props.editing.id,
        { values: values.value },
        props.devUser,
      );
      toast.success("Source saved", "Changes apply from the next sync.");
    } else if (kind.value.mode === "upload") {
      // One at a time: each upload registers the folder on first use, and
      // two racing firsts would both try to.
      for (const f of files.value)
        await uploadFile(slug.value, f, props.devUser);
      toast.success(
        files.value.length === 1
          ? `${files.value[0].name} uploaded`
          : `${files.value.length} files uploaded`,
      );
    } else {
      const res = await addSourceFromForm(
        slug.value,
        { kind: kind.value.key, values: values.value },
        props.devUser,
      );
      if (props.runnerAvailable) await triggerIngest(slug.value, props.devUser);
      const when = !props.runnerAvailable
        ? ""
        : props.runner === "external"
          ? "First sync starts within 10 minutes."
          : "First sync started.";
      // ADR 0053 — who decides about this assistant can change with what it
      // reads. Say so at the moment it happens, not in a tab nobody has open.
      const ac = res.access_class;
      const moved = ac?.changed
        ? `Access class is now ${ac.label} (was ${ac.was}). ${ac.route}`
        : "";
      if (res.warnings.length)
        toast.error("Added, but check this", res.warnings[0]);
      else
        toast.success(
          `${kind.value.label} added`,
          [when, moved].filter(Boolean).join(" ") || undefined,
        );
    }
    emit("done");
  } catch (e) {
    // Validation messages are written for the admin — show them in place,
    // next to the field they are about, not in a toast that scrolls away.
    error.value = e instanceof AdminError ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <form v-write class="source-editor" @submit.prevent="submit">
    <div
      v-if="!editing"
      class="tiles"
      role="radiogroup"
      aria-label="Kind of source"
    >
      <button
        v-for="k in kinds"
        :key="k.key"
        type="button"
        role="radio"
        class="tile"
        :class="{ 'tile--on': k.key === kindKey, 'tile--off': !k.available }"
        :aria-checked="k.key === kindKey"
        :aria-disabled="!k.available"
        @click="pick(k)"
      >
        <span class="tile__label">{{ k.label }}</span>
        <span class="tile__desc">{{
          k.available ? k.description : k.unavailable_reason
        }}</span>
      </button>
    </div>

    <template v-if="kind">
      <p v-if="!runnerAvailable && !editing" class="notice">
        Saved here, but <strong>not synced on this deployment</strong> — the job
        that runs these sources is not deployed here
        (<code>INCLUDE_DB_SOURCES_CRON</code>).
      </p>

      <label v-if="!editing" class="field">
        <span class="field__label">For assistant</span>
        <select v-model="slug" required>
          <option value="" disabled>Choose…</option>
          <option v-for="a in assistants" :key="a.slug" :value="a.slug">
            {{ a.name }}
          </option>
        </select>
      </label>

      <label v-if="kind.mode === 'upload'" class="field">
        <span class="field__label">Files</span>
        <input type="file" multiple @change="onFiles" />
      </label>

      <template v-for="f in mainFields" :key="f.key">
        <label class="field">
          <span class="field__label">{{ f.label }}</span>
          <select
            v-if="f.kind === 'resource'"
            v-model="values[f.key]"
            :disabled="!!editing"
          >
            <option value="" disabled>
              {{ grantedResources.length ? "Choose…" : "No granted sites yet" }}
            </option>
            <option v-for="r in grantedResources" :key="r.id" :value="r.id">
              {{ r.title || r.address.replace(/^https:\/\//, "") }}
            </option>
          </select>
          <select v-else-if="f.kind === 'library'" v-model="values[f.key]">
            <option value="">All libraries</option>
            <option
              v-for="d in pickedResource?.drives ?? []"
              :key="d.id"
              :value="d.name"
            >
              {{ d.name }}
            </option>
          </select>
          <textarea
            v-else-if="f.kind === 'lines'"
            v-model="values[f.key] as string"
            rows="3"
            :placeholder="f.placeholder"
            spellcheck="false"
          />
          <span v-if="f.help" class="field__help">{{ f.help }}</span>
        </label>
      </template>

      <div v-if="advancedFields.length" class="more">
        <button
          type="button"
          class="link"
          :aria-expanded="moreOpen"
          @click="moreOpen = !moreOpen"
        >
          {{ moreOpen ? "Fewer options" : "More options" }}
        </button>
        <div v-if="moreOpen" class="more__body">
          <template v-for="f in advancedFields" :key="f.key">
            <label v-if="f.kind === 'bool'" class="field field--check">
              <input v-model="values[f.key] as boolean" type="checkbox" />
              <span>{{ f.label }}</span>
            </label>
            <label v-else class="field">
              <span class="field__label">{{ f.label }}</span>
              <input
                v-if="f.kind === 'int'"
                v-model.number="values[f.key] as number"
                type="number"
                :min="f.min ?? undefined"
                :max="f.max ?? undefined"
              />
              <textarea
                v-else
                v-model="values[f.key] as string"
                rows="2"
                :placeholder="f.placeholder"
                spellcheck="false"
              />
              <span v-if="f.help" class="field__help">{{ f.help }}</span>
            </label>
          </template>
        </div>
      </div>

      <p v-if="kind.visibility" class="field__help">
        Who can see it: {{ kind.visibility.toLowerCase() }}.
      </p>

      <p v-if="error" class="error" role="alert">{{ error }}</p>

      <div class="actions">
        <button class="btn btn--primary" type="submit" :disabled="!canSubmit">
          {{ busy ? "Working…" : submitLabel }}
        </button>
      </div>
    </template>
  </form>
</template>

<style scoped>
.source-editor {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 0.5rem;
}
.tile {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.6rem 0.7rem;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.tile:hover:not(.tile--off) {
  border-color: var(--color-accent);
}
.tile--on {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 1px var(--color-accent);
}
.tile--off {
  cursor: default;
  opacity: 0.6;
  background: transparent;
}
.tile__label {
  font-weight: 650;
}
.tile__desc {
  font-size: 0.8rem;
  color: var(--color-muted);
  line-height: 1.35;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.field--check {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}
.field__label {
  font-size: 0.85rem;
  font-weight: 600;
}
.field__help {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.field textarea,
.field select,
.field input[type="number"] {
  font: inherit;
  font-size: 0.92rem;
  color: var(--color-ink);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.45rem 0.6rem;
  width: 100%;
}
.field input[type="number"] {
  max-width: 9rem;
}
.field textarea {
  resize: vertical;
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.85rem;
  line-height: 1.5;
}
.field textarea:focus,
.field select:focus,
.field input:focus {
  outline: none;
  border-color: var(--color-accent);
}
.more__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-2);
  padding-left: 0.75rem;
  border-left: 2px solid var(--color-border);
}
.notice {
  margin: 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.85rem;
  color: var(--color-warn-ink);
  background: var(--color-warn-bg);
  border-radius: var(--radius-sm);
}
.error {
  margin: 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.88rem;
  color: var(--color-error);
  background: var(--color-error-bg);
  border-radius: var(--radius-sm);
}
.actions {
  display: flex;
  justify-content: flex-end;
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.85rem;
  color: var(--color-accent);
  cursor: pointer;
}
</style>
